import React, { useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { useNewsletterSubscription } from '../hooks/useNewsletterSubscription';

interface NewsletterSubscriptionFormProps {
  className?: string;
  placeholder?: string;
  buttonAriaLabel?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Newsletter subscription form component
 */
export function NewsletterSubscriptionForm({
  className = '',
  placeholder = 'e-mail address',
  buttonAriaLabel = 'Subscribe to newsletter',
  onSuccess,
  onError,
}: NewsletterSubscriptionFormProps) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const newsletterMutation = useNewsletterSubscription();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setEmailError('');

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      await newsletterMutation.mutateAsync(email);
      setEmail(''); // Clear form on success
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error?.message || 'Failed to subscribe to newsletter';
      setEmailError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`flex flex-col gap-y-2 ${className}`}
    >
      <div className='flex items-center justify-between py-2 w-full max-w-md mx-auto relative'>
        <input
          type='email'
          placeholder={placeholder}
          value={email}
          onChange={handleInputChange}
          disabled={newsletterMutation.isPending}
          className={`bg-transparent border-b border-white text-white placeholder:text-white/70 focus:outline-none w-full mr-2 pb-3 ${
            emailError ? 'border-red-400' : ''
          }`}
          aria-label='Email address for newsletter subscription'
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'email-error' : undefined}
        />
        <button
          type='submit'
          disabled={newsletterMutation.isPending || !email.trim()}
          className='absolute right-0 top-2 w-6 h-6 border border-white rounded-full flex items-center justify-center hover:bg-[#F1F1E9] hover:text-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          aria-label={buttonAriaLabel}
        >
          {newsletterMutation.isPending ? (
            <div className='w-3 h-3 border border-current border-t-transparent rounded-full animate-spin' />
          ) : (
            <FaArrowRight size={16} />
          )}
        </button>
      </div>

      {emailError && (
        <p
          id='email-error'
          className='text-red-400 text-sm mt-1'
          role='alert'
          aria-live='polite'
        >
          {emailError}
        </p>
      )}
    </form>
  );
}
